import { useCallback, useEffect, useState } from "react";
import { useModalChrome } from "../lib/modalUx";
import { sizedPhotoUrl } from "../lib/thumb";
import { CloseIcon } from "../ui";

// Full-screen photo viewer. Module-level controller (same pattern as Toaster)
// so any card can call openPhoto() without prop drilling.

interface Photo {
  url: string;
  common_name: string;
  scientific_name: string;
}

let listener: ((p: Photo | null) => void) | null = null;

export function openPhoto(p: Photo): void {
  if (p.url) listener?.(p);
}

export function PhotoViewer() {
  const [photo, setPhoto] = useState<Photo | null>(null);

  useEffect(() => {
    listener = setPhoto;
    return () => {
      listener = null;
    };
  }, []);

  useEffect(() => {
    if (!photo) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPhoto(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [photo]);

  const close = useCallback(() => setPhoto(null), []);
  useModalChrome(photo !== null, close);

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-ink/90 p-4 backdrop-blur-sm"
      onClick={() => setPhoto(null)}
      role="dialog"
      aria-modal="true"
      aria-label={`Photo of ${photo.common_name}`}
    >
      <button
        type="button"
        onClick={() => setPhoto(null)}
        className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-lg text-white hover:bg-white/25"
        aria-label="Close photo"
      >
        <CloseIcon className="h-4 w-4" />
      </button>
      {/* Scroll+pinch container: touch-action:pinch-zoom + overflow:auto
          lets iOS/Chrome do native gesture zoom on the image without any
          extra JS. Falls back to a plain viewer on desktop. */}
      <div
        className="relative max-h-[82vh] w-full max-w-full overflow-auto rounded-xl shadow-card"
        style={{ touchAction: "pinch-zoom" }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => {
          // Double-tap zoom on desktop: bump the image to natural size briefly.
          const img = e.currentTarget.querySelector("img");
          if (!img) return;
          img.classList.toggle("scale-150");
        }}
      >
        <img
          src={sizedPhotoUrl(photo.url, "large")}
          alt={photo.common_name}
          className="mx-auto block max-h-[82vh] max-w-full rounded-xl object-contain transition-transform duration-200"
        />
      </div>
      <div
        className="mt-4 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <p className="font-display text-lg font-bold text-paper">
          {photo.common_name}
        </p>
        <p className="text-sm italic text-paper/60">
          {photo.scientific_name}
        </p>
      </div>
    </div>
  );
}
